<template>
    <v-container  v-resize="onResize">
        <v-layout row wrap align-center>
            <v-flex xs12 md6>
                <v-btn to="/StatisticalInformation/Divisions" color="white">
                    <v-icon dark left>arrow_back</v-icon>Back to divisions
                </v-btn>
            </v-flex>
        </v-layout>
        <v-layout row wrap pt-3 pb-4 align-center>
            <v-flex xs12 md6>
                <h1>Divisions for the {{ numberToOrdinal(parliament) }} Parliament</h1>
            </v-flex>
        </v-layout>

        <loader v-if="loading" />
        <template v-if="!loading" >
            <statistical-member-or-senator-selector :value="selectedMpOrSenator" @input="selectedMpOrSenator = $event" :multiple="false" :alternateText="true" />
            <br/>
            <v-menu v-if="this.isMobile" d-inline  :close-on-content-click="true">
                <v-btn outline small slot="activator">
                    Sort <v-icon>sort</v-icon>
                </v-btn>
                <v-list>
                    <v-subheader>
                        Sort by:
                    </v-subheader>
                    <v-list-tile v-for="(item, index) in headers.filter(h => h.text != '' && h.sortable != false)" :key="item.value" @click="changeSort(item.value)">
                        <v-list-tile-title>
                            {{ item.text }}
                            <v-icon v-if="pagination.sortBy === item.value">{{pagination.descending ? 'arrow_downward':'arrow_upward'}}</v-icon>
                        </v-list-tile-title>
                    </v-list-tile>
                </v-list>
            </v-menu>

            <v-layout row wrap align-center>
                <div class="mr-3">
                    <v-text-field :class="{mobileTextField: isMobile}" v-if="!this.loading" v-model="search" append-icon="search" label="Search" single-line hide-details></v-text-field>
                </div>

                <division-selector class="mr-3" :topics="contexts" :value="selectedContext" @input="selectedContext = $event"></division-selector>

                <v-btn-toggle v-model="divisionType" class="transparent handbook_toggleButton">
                    <v-tooltip top :disabled="this.$vuetify.breakpoint.name !== 'md'">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="handbook_button" :value="'floorCrossing'">
                                <span class="long-button-text chamberButton">Split vote</span>
                            </v-btn>
                        </template>
                        <span>Split vote</span>
                    </v-tooltip>
                    <v-tooltip top :disabled="this.$vuetify.breakpoint.name !== 'md'">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="handbook_button" :value="'governmentDefeated'">
                                <span class="long-button-text chamberButton">Government defeated</span>
                            </v-btn>
                        </template>
                        <span>Government defeated</span>
                    </v-tooltip>
                </v-btn-toggle>

                <v-btn-toggle v-model="viewType" class="transparent handbook_toggleButton">
                    <v-tooltip top :disabled="this.$vuetify.breakpoint.name !== 'md'">
                        <template v-slot:activator="{ on }">
                            <v-btn v-on="on" class="handbook_button" :value="'partyVotes'">
                                <span class="long-button-text chamberButton">Show party votes</span>
                            </v-btn>
                        </template>
                        <span>Show party votes</span>
                    </v-tooltip>
                </v-btn-toggle>                
            </v-layout>

            <br/>

            <div class="stats-table">
                <v-data-table
                    :headers="headers" 
                    :items="itemsFiltered"
                    :pagination.sync="pagination"
                    :rows-per-page-items="ROWS_LISTITEMS" 
                    :class="{ mobileDataTable: isMobile, 'elevation-1': true }"
                    :hide-headers="isMobile"
                    :loading="loading"
                    :search="search"
                    :disable-initial-sort="true">
                    <template v-slot:items="props">
                        <template v-if="isMobile">
                            <router-link class="tr" :to="{ name: 'Division', params: { id: props.item.Id, chamber: props.item.ChamberType }}">
                                <td>
                                    <div class="flex-content">
                                        <div class="flex-item">
                                            <h3>
                                                {{ props.item.Date | stringDateFormat }}
                                            </h3>
                                        </div>
                                        <div class="flex-item">
                                            <div class="flex-item" data-label="Title">
                                                {{ props.item.Title | divisionTitle }}
                                            </div>

                                            <template v-if="props.item.VotesSummary.length === 0">
                                                <div class="flex-item flex-item-col flex-item-col-50" data-label="Ayes">
                                                    {{ props.item.Ayes }}
                                                </div>
                                                <div class="flex-item flex-item-col flex-item-col-50" data-label="Noes">
                                                    {{ props.item.Noes }}
                                                </div>
                                            </template>

                                            <template v-else>
                                                <div class="flex-item party-votes" data-label="Ayes">
                                                    <div v-for="v in props.item.VotesSummary.filter(v => v.Result === 0)" :key="v.Id">                                        
                                                        <v-chip disabled small light>
                                                            <v-avatar :color="v.PartyColour != null ? v.PartyColour : '#919191'" >
                                                                <span :style="{'color': (v.PartyColour == null || v.PartyColour.toLowerCase() != '#bfff00' ? '#fff' : '#000') }">{{ v.Count }}</span>
                                                            </v-avatar>
                                                            {{ v.Party }}
                                                        </v-chip>                                        
                                                    </div>                                                     
                                                </div>
                                                <div class="flex-item party-votes" data-label="Noes">
                                                    <div v-for="v in props.item.VotesSummary.filter(v => v.Result === 1)" :key="v.Id">                                        
                                                        <v-chip disabled small light>
                                                            <v-avatar :color="v.PartyColour != null ? v.PartyColour : '#919191'" >
                                                                <span :style="{'color': (v.PartyColour == null || v.PartyColour.toLowerCase() != '#bfff00' ? '#fff' : '#000') }">{{ v.Count }}</span>
                                                            </v-avatar>
                                                            {{ v.Party }}
                                                        </v-chip>                                        
                                                    </div>                                                                                                        
                                                </div>
                                            </template>

                                            <div class="flex-item flex">
                                                <v-chip v-if="props.item.Outcome === 0" small label color="green">Agreed to</v-chip>
                                                <v-chip v-if="props.item.Outcome === 1" small label color="red">Negatived</v-chip>
                                            </div>
                                        </div>
                                    </div>
                                </td>
                            </router-link>
                        </template>
                        <template v-if="!isMobile">
                            <router-link class="tr" :to="{ name: 'Division', params: { id: props.item.Id }}">
                                <td class="text-xs-left"><strong>{{ props.item.Date | stringDateFormat }}</strong></td>
                                <td class="text-xs-left">{{ props.item.Title | divisionTitle }}</td>
                                                                
                                <template v-if="props.item.VotesSummary.length === 0">
                                    <td class="text-xs-left">{{ props.item.Ayes }}</td>
                                    <td class="text-xs-left">{{ props.item.Noes }}</td>
                                </template>
                                <template v-else>
                                <td class="text-xs-left party-votes" style="vertical-align:top">                                                                        
                                        <div v-for="v in props.item.VotesSummary.filter(v => v.Result === 0)" :key="v.Id">                                        
                                            <v-chip disabled small light>
                                                <v-avatar :color="v.PartyColour != null ? v.PartyColour : '#919191'" >
                                                    <span :style="{'color': (v.PartyColour == null || v.PartyColour.toLowerCase() != '#bfff00' ? '#fff' : '#000') }">{{ v.Count }}</span>
                                                </v-avatar>
                                                {{ v.Party }}
                                            </v-chip>                                        
                                        </div>                                    
                                </td>
                                <td class="text-xs-left party-votes" style="vertical-align:top">                                                                                                         
                                        <div v-for="v in props.item.VotesSummary.filter(v => v.Result === 1)" :key="v.Id">                                        
                                            <v-chip disabled small light>
                                                <v-avatar :color="v.PartyColour != null ? v.PartyColour : '#919191'" >
                                                    <span :style="{'color': (v.PartyColour == null || v.PartyColour.toLowerCase() != '#bfff00' ? '#fff' : '#000') }">{{ v.Count }}</span>
                                                </v-avatar>
                                                {{ v.Party }}
                                            </v-chip>                                        
                                        </div>                                                                        
                                </td>
                                </template>
                                <td class="text-xs-left">
                                    <v-chip v-if="props.item.Outcome === 0" small label color="green">Agreed to</v-chip>
                                    <v-chip v-if="props.item.Outcome === 1" small label color="red">Negatived</v-chip>
                                </td>
                            </router-link>
                        </template>
                    </template>
                    <template v-slot:no-results>
                        <v-alert :value="true" color="error" icon="warning">
                            Your search for "{{ search }}" found no results.
                        </v-alert>
                    </template>
                    <template v-slot:footer>
                    </template>
                </v-data-table>
            </div>
            <br/>
            <v-layout row wrap>
                <v-flex xs12>
                    <v-btn class="csvButton" @click="exportCsv" v-on:keyup.enter="exportCsv" color="primary">
                        <v-icon aria-hidden="true" :class="csvDownloadClass">{{csvDownloadIcon}}</v-icon>
                        <span>&nbsp;Download as CSV</span>
                    </v-btn>
                </v-flex>
            </v-layout>         
        </template>
    </v-container>
</template>
<script>
import Loader from '@/components/Loader.vue';
import DivisionSelector from './DivisionSelector.vue';
import StatisticalMemberOrSenatorSelector from './StatisticalMemberOrSenatorSelector.vue';
import axios from 'axios';
import moment from 'moment'
window.Handbook = window.Handbook || {}
const apiUri = window.Handbook.ApiUri || "https://localhandbookapi.aph.gov.au/";
axios.defaults.baseURL = apiUri;

export default {
    props: ['parliament', 'chamber'],
    created() {
        this.initialiseItems();
    },
    components: {
        Loader,
        StatisticalMemberOrSenatorSelector,
        DivisionSelector
    },
    mounted() {
        this.onResize();
    },
    methods: {
        onResize() {
            if (window.innerWidth < 769){
                this.isMobile = true;
            }
            else{
                this.isMobile = false;
            }
        },
        changeSort(column) {
            if (this.pagination.sortBy === column) {
                this.pagination.descending = !this.pagination.descending
            } 
            else {
                this.pagination.sortBy = column
                this.pagination.descending = false
            }
        },
        initialiseItems() {
            this.loading = true;
            const floorCrossing = this.divisionType === 'floorCrossing';
            const governmentDefeated = this.divisionType === 'governmentDefeated';
            const showPartyVotes = this.viewType === 'partyVotes';
            const url = `StatisticalInformation/DivisionsForParliament?chamber=${this.selectedChamber}&parliament=${this.parliament}&floorCrossing=${floorCrossing}&governmentDefeated=${governmentDefeated}&partyVotes=${showPartyVotes}`;
            axios.get(`api/${url}`)
                .then(response => {
                    this.items = response.data;
                    this.loading = false;
                });
        },
        exportCsv: function () {
            this.csvDownloading = true;
            const records = this.items.map(i => 
                ({ 
                    Date: (new Date(i.Date)).toLocaleDateString(),
                    Title: i.Title,
                    Ayes: i.Ayes,
                    Noes: i.Noes,
                    Outcome: i.Outcome === 0 ? 'Agreed to': 'Negatived'
                }));
            let headers = this.headers.map(h => h.text);
            const filename = `Divisions for ${this.parliament}`;
            const comment = `Data current as of ${(new Date()).toLocaleDateString()}`;
            this.$store.dispatch('GETCSV', { DATA: records, HEADERS: headers, FILENAME: filename, COMMENT: comment })
                .catch(this.showError)
                .finally(() => this.csvDownloading = false);
        },
        stringDate (value) {
            if (value && value != 'undefined' && value != 'Undefined' 
            && value != '1900-01-01' 
            && value != '1900-01-01T00:00:00Z' 
            && value != '1900-01-1' 
            && value != '0001-01-01'
            && value != '0001-01-01T00:00:00Z') {
                var dateDMY = moment(String(value));
                if (dateDMY.isValid()){
                    return dateDMY.format('DD/MM/YYYY');
                }
                else {
                    var dateFormat =  moment(String(value), 'YYYY-DD-MM');
                    if (dateFormat.isValid()){
                        return dateFormat.format('DD/MM/YYYY');
                    }
                    else {
                        return ''
                    }
                }
            } else {
                return 'present'
            }
        }
    }, 
    filters: {
        divisionTitle: function (value) {
            if(value.includes(';')) {
                let splitTitle = value.split(';')
                .slice(0, -1);
                return splitTitle.join(' - ');
            }
            return value;
        }
    },
    computed: {
            contexts() {
                return [...[...new Set(this.items.map(i => i.Title.split(';')[0]))].sort()]
            },
            selectedChamber() {
                return this.selectedMpOrSenator === 'mp' ? 0 : 1;
            },
            csvDownloadIcon() {
                return this.csvDownloading ? 'cached' : 'text_snippet';
            },
            csvDownloadClass() {
                let csvClass = 'mr-1';
                if (this.csvDownloading){
                    csvClass = csvClass + ' loading'
                }
                return csvClass;
            },
            detailedCsvDownloadIcon() {
                return this.detailedCsvDownloading ? 'cached' : 'text_snippet';
            },
            detailedCsvDownloadClass() {
                let csvClass = 'mr-1';
                if (this.detailedCsvDownloading){
                    csvClass = csvClass + ' loading'
                }
                return csvClass;
            },
            itemsFiltered() {
                return this.selectedContext ? this.items.filter(i => i.Title.split(';')[0] === this.selectedContext) : this.items;
            }
    },
    watch: {
        selectedMpOrSenator: function () {
            this.initialiseItems();
        },
        divisionType: function () {
            this.initialiseItems();
        },
        viewType: function() {
            this.initialiseItems();
        }
    },
    data() {
        return {
            isMobile: false,
            ROWS_LISTITEMS: [5, 10, 50, {
                "text": "$vuetify.dataIterator.rowsPerPageAll",
                "value": -1
            }],
            title: 'Divisions',
            chartType: 'perYear',
            headers: [
                { text: 'Date', value: 'Date' },
                { text: 'Title', value: 'Title' },
                { text: 'Ayes', value: 'Ayes' },
                { text: 'Noes', value: 'Noes' },
                { text: 'Outcome', value: 'Outcome' },
            ],
            items: [],
            selectedMpOrSenator: this.chamber == 0 ? 'mp' : 'senator',
            search: '',
            divisionType: '',
            viewType: '',
            selectedContext: null,
            loading: true,
            csvDownloading: false,
            detailedCsvDownloading: false,
            pagination: {
                rowsPerPage: this.$store.state.ROWS_PER_PAGE
            }
        }
    }
}
</script>
<style lang="stylus" scoped>        
    .v-datatable .tr {
        display:table-row;
    }

    .v-datatable a.tr {
        color: #0e1531de;
        text-decoration:none;
        border: 1px solid #dbdbdb;
        border-left: none;
        border-right: none;
    }

    .v-datatable tbody td {
        vertical-align: middle;
    }

    .party-votes .theme--light.v-chip {
        background: #dddddd;
        color: #000;
    }
    .party-votes .theme--light.v-chip .v-avatar{
        color: #fff;
    }
</style>